import React from 'react';
import { jsPDF } from 'jspdf';
import { useOnboardingContext } from '../../../../contexts/OnboardingContext';
import styles from './PdfDownload.module.css';
import cx from 'classnames';
import Button from '../../../../components/Button';

const PdfDownload: React.FC = () => {
    const { formData } = useOnboardingContext();

    const formatPrice = (price: number): string => {
        if (price === 0) {
            return 'kostenlos';
        }
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR',
        }).format(price);
    };

    const handleDownloadPdf = () => {
        const doc = new jsPDF();
        let yPosition = 20;
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 20;
        const maxWidth = pageWidth - (margin * 2);
        const lineHeight = 7;
        const sectionSpacing = 10;

        // Helper function to add text with word wrap
        const addText = (text: string, fontSize: number, isBold: boolean = false, color?: string) => {
            doc.setFontSize(fontSize);
            if (isBold) {
                doc.setFont('helvetica', 'bold');
            } else {
                doc.setFont('helvetica', 'normal');
            }
            if (color) {
                doc.setTextColor(color);
            }
            
            const lines = doc.splitTextToSize(text, maxWidth);
            lines.forEach((line: string) => {
                if (yPosition > 280) {
                    doc.addPage();
                    yPosition = 20;
                }
                doc.text(line, margin, yPosition);
                yPosition += lineHeight;
            });
            
            doc.setTextColor(0, 0, 0);
            doc.setFont('helvetica', 'normal');
        };

        addText('TS Workwear', 18, true);
        yPosition += sectionSpacing;

        addText('Benutzerdaten', 14, true);
        
        if (formData.selectedTypes.length > 0) {
            const typesText = formData.selectedTypes.map(type => type.name).join(', ');
            addText(`Branche: ${typesText}`, 10);
        }

        if (formData.userData.company) {
            addText(`Firma: ${formData.userData.company}`, 10);
        }
        if (formData.userData.customTypeName) {
            addText(`Andere Branche: ${formData.userData.customTypeName}`, 10);
        }
        if (formData.userData.firstName) {
            addText(`Vorname: ${formData.userData.firstName}`, 10);
        }
        if (formData.userData.lastName) {
            addText(`Nachname: ${formData.userData.lastName}`, 10);
        }
        if (formData.userData.street) {
            addText(`Straße: ${formData.userData.street}`, 10);
        }
        if (formData.userData.houseNumber) {
            addText(`Hausnummer: ${formData.userData.houseNumber}`, 10);
        }       
        if (formData.userData.postalCode) {
            addText(`Postleitzahl: ${formData.userData.postalCode}`, 10);
        }
        if (formData.userData.city) {
            addText(`Ort: ${formData.userData.city}`, 10);
        }
        if (formData.userData.email) {
            addText(`E-Mail: ${formData.userData.email}`, 10);
        }
        if (formData.userData.phoneNumber) {
            addText(`Telefonnummer: ${formData.userData.phoneNumber}`, 10);
        }
        if (formData.userData.memberCount) {
            addText(`Anzahl Mitarbeiter: ${formData.userData.memberCount}`, 10);
        }
        if (formData.userData.additionalInformation) {
            addText(`Zusätzliche Informationen: ${formData.userData.additionalInformation}`, 10);
        }
        yPosition += sectionSpacing;

        addText('CI', 14, true);
        
        if (formData.primaryColor) {
            addText(`Primärfarbe: ${formData.primaryColor}`, 10);
        }
        if (formData.secondaryColor) {
            addText(`Sekundärfarbe: ${formData.secondaryColor}`, 10);
        }
        if (formData.font) {
            addText(`Schriftart: ${formData.font}`, 10);
        }
        if (formData.style) {
            addText(`Stil: ${formData.style}`, 10);
        }
        
        const logosWithFiles = formData.logos.filter(logo => logo.file !== null);
        if (logosWithFiles.length > 0) {
            const logoLabels = logosWithFiles.map(logo => logo.label || logo.file?.name || 'Logo').join(', ');
            addText(`Logos: ${logoLabels}`, 10);
        }
        yPosition += sectionSpacing;

    
        if (formData.selectedArticles.length > 0) {
            addText(`Ausgewählte Artikel (${formData.selectedArticles.length})`, 14, true);
            formData.selectedArticles.forEach((article, index) => {
                addText(`${index + 1}. ${article.name} - ${formatPrice(article.price)}`, 10);
            });
        }
        
        const timestamp = new Date().toISOString().split('T')[0];
        const filename = `onboarding-konfiguration-${timestamp}.pdf`;

        doc.save(filename);
    };

    return (
        <div className={cx(styles['pdf-download-container'])}>
            <Button
                type="button"
                fn={handleDownloadPdf}
                classes={cx(styles['pdf-download-button'])}
                text="PDF herunterladen"
                hierarchy="primary"
            />
        </div>
    );
};

export default PdfDownload;
